import { Button } from "@legal-500/legal500-design-system";
import { CmsImagePreview } from "../../../components/CMS/CmsImagePreview/CmsImagePreview";
import {
  plainText,
  toAccordionContent,
  toBannerContent,
  toButtonsContent,
  toCallToActionContent,
  toGalleryContent,
  toHeroContent,
  toIconListContent,
  toPeopleContent,
  toStatsContent,
  toStepsContent,
  toTextContent,
  toVideoContent,
} from "./blockContent";
import type { BlockBackground } from "./types";
import styles from "./BlockPreview.module.css";

type PreviewRow = {
  title: string;
  text?: string;
  image?: { url: string; alt: string };
};

type SectionPreviewProps = {
  heading: string;
  intro?: string;
  background: BlockBackground;
  rows: PreviewRow[];
  emptyText?: string;
  onEdit?: () => void;
};

function htmlToText(html: string): string {
  if (!html) return "";
  return (new DOMParser().parseFromString(html, "text/html").body.textContent ?? "").trim();
}

const BACKGROUND_LABELS: Record<BlockBackground, string> = {
  default: "White background",
  muted: "Grey background",
  blue: "Blue background",
  dark: "Dark background",
  brand: "Brand background",
};

type SectionPreviewHeaderProps = {
  heading: string;
  intro?: string;
  background: BlockBackground;
  onEdit?: () => void;
};

export function SectionPreviewHeader({
  heading,
  intro,
  background,
  onEdit,
}: SectionPreviewHeaderProps) {
  const introText = intro ? htmlToText(intro) : "";
  return (
    <div className={styles.sectionPreviewHeader}>
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>{heading || "No heading"}</div>
        {introText && <div className={styles.cardText}>{introText}</div>}
      </div>
      <span className={styles.cardOrderBadge}>{BACKGROUND_LABELS[background]}</span>
      {onEdit && (
        <Button variant="outlined" color="default" onClick={onEdit}>
          Edit
        </Button>
      )}
    </div>
  );
}

function SectionPreview({
  heading,
  intro,
  background,
  rows,
  emptyText,
  onEdit,
}: SectionPreviewProps) {
  return (
    <div className={styles.cardGrid}>
      <SectionPreviewHeader
        heading={heading}
        intro={intro}
        background={background}
        onEdit={onEdit}
      />
      {rows.length === 0 && emptyText && <p>{emptyText}</p>}
      {rows.map((row, index) => (
        <div key={`${index}-${row.title}`} className={styles.card}>
          {row.image && (
            <CmsImagePreview
              key={row.image.url}
              value={row.image.url}
              alt={row.image.alt}
              initial={row.title.charAt(0).toUpperCase() || "?"}
              size="small"
            />
          )}
          <div className={styles.cardBody}>
            <div className={styles.cardTitle}>{row.title}</div>
            {row.text && <div className={styles.cardText}>{row.text}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

type SectionBlockPreviewProps = {
  type: string;
  content: Record<string, unknown> | null;
  onEdit?: () => void;
};

export const SECTION_BLOCK_TYPES = [
  "block_hero",
  "block_text",
  "block_stats",
  "block_people",
  "block_icon_list",
  "block_buttons",
  "block_gallery",
  "block_banner",
  "block_steps",
  "block_video",
  "block_accordion",
  "block_call_to_action",
];

export function SectionBlockPreview({ type, content, onEdit }: SectionBlockPreviewProps) {
  switch (type) {
    case "block_hero": {
      const hero = toHeroContent(content);
      return (
        <SectionPreview
          heading={hero.title}
          background={hero.background}
          onEdit={onEdit}
          rows={[
            ...(hero.imageUrl
              ? [
                  {
                    title: hero.imageAlt || "Image has no description",
                    image: { url: hero.imageUrl, alt: hero.imageAlt || "Hero image" },
                  },
                ]
              : []),
            ...hero.links.map(link => ({ title: link.label, text: link.href })),
          ]}
        />
      );
    }
    case "block_text": {
      const textBlock = toTextContent(content);
      const body = htmlToText(textBlock.body);
      return (
        <SectionPreview
          heading={textBlock.heading}
          background={textBlock.background}
          onEdit={onEdit}
          rows={[{ title: "Body", text: body || "No body text yet." }]}
        />
      );
    }
    case "block_stats": {
      const stats = toStatsContent(content);
      return (
        <SectionPreview
          heading={stats.heading}
          background={stats.background}
          onEdit={onEdit}
          emptyText="No stats yet."
          rows={stats.stats.map(stat => ({
            title: `${stat.value} ${stat.label}`.trim(),
            text: stat.tooltip,
          }))}
        />
      );
    }
    case "block_people": {
      const people = toPeopleContent(content);
      return (
        <SectionPreview
          heading={people.heading}
          intro={people.intro}
          background={people.background}
          onEdit={onEdit}
          emptyText="No people yet."
          rows={people.people.map(person => ({
            title: person.name,
            text: [person.role, person.email].filter(Boolean).join(" · "),
            image: { url: person.imageUrl, alt: `${person.name || "Person"} photo` },
          }))}
        />
      );
    }
    case "block_icon_list": {
      const iconList = toIconListContent(content);
      return (
        <SectionPreview
          heading={iconList.heading}
          intro={iconList.intro}
          background={iconList.background}
          onEdit={onEdit}
          emptyText="No items yet."
          rows={iconList.items.map(item => ({
            title: item.heading,
            text: item.text,
            image: { url: item.iconUrl, alt: `${item.heading || "Item"} icon` },
          }))}
        />
      );
    }
    case "block_buttons": {
      const buttons = toButtonsContent(content);
      return (
        <SectionPreview
          heading="Buttons"
          background={buttons.background}
          onEdit={onEdit}
          emptyText="No buttons yet."
          rows={buttons.buttons.map(button => ({
            title: `${button.label} (${button.variant === "secondary" ? "Secondary" : "Primary"})`,
            text: button.href,
          }))}
        />
      );
    }
    case "block_gallery": {
      const gallery = toGalleryContent(content);
      return (
        <SectionPreview
          heading={gallery.heading}
          intro={gallery.intro}
          background={gallery.background}
          onEdit={onEdit}
          emptyText="No images yet."
          rows={gallery.images.map((image, index) => ({
            title: image.caption || image.alt || `Image ${index + 1}`,
            text: image.url ? undefined : "No image uploaded",
            image: { url: image.url, alt: image.alt || `Gallery image ${index + 1}` },
          }))}
        />
      );
    }
    case "block_banner": {
      const banner = toBannerContent(content);
      const buttons = [banner.primaryButton, banner.secondaryButton].filter(
        button => button !== undefined,
      );
      return (
        <SectionPreview
          heading={banner.title}
          intro={banner.tagline}
          background={banner.background ?? "dark"}
          onEdit={onEdit}
          rows={[
            ...(banner.heading ? [{ title: "Eyebrow", text: banner.heading }] : []),
            ...buttons.map(button => ({ title: button.label, text: button.href })),
            ...(banner.videoUrl ? [{ title: "Background video", text: banner.videoUrl }] : []),
          ]}
        />
      );
    }
    case "block_steps": {
      const steps = toStepsContent(content);
      return (
        <SectionPreview
          heading={steps.heading ?? ""}
          intro={steps.intro}
          background={steps.background ?? "default"}
          onEdit={onEdit}
          emptyText="No steps yet."
          rows={steps.items.map((item, index) => ({
            title: `${index + 1}. ${item.heading || plainText(item.text) || "No text yet"}`,
            ...(item.image
              ? { image: { url: item.image, alt: item.imageAlt || `Step ${index + 1}` } }
              : {}),
          }))}
        />
      );
    }
    case "block_video": {
      const video = toVideoContent(content);
      return (
        <SectionPreview
          heading={video.heading || video.title}
          intro={video.intro}
          background={video.background ?? "default"}
          onEdit={onEdit}
          rows={[{ title: video.title || "No title", text: video.url || "No video link yet." }]}
        />
      );
    }
    case "block_accordion": {
      const accordion = toAccordionContent(content);
      return (
        <SectionPreview
          heading={accordion.heading ?? ""}
          background={accordion.background ?? "default"}
          onEdit={onEdit}
          emptyText="No questions yet."
          rows={accordion.items.map(item => ({
            title: item.question,
            text: plainText(item.answer),
          }))}
        />
      );
    }
    case "block_call_to_action": {
      const callToAction = toCallToActionContent(content);
      return (
        <SectionPreview
          heading={callToAction.heading ?? ""}
          intro={callToAction.text}
          background={callToAction.background ?? "brand"}
          onEdit={onEdit}
          emptyText="No buttons yet."
          rows={callToAction.buttons.map(button => ({
            title: `${button.label} (${button.variant === "secondary" ? "Secondary" : "Primary"})`,
            text: button.href,
          }))}
        />
      );
    }
    default:
      return null;
  }
}
